import { useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts';

const BAR = '#2a78d6';
const BAR_FOCUS = '#1baf7a';
const AVG = '#eb6834';

function mean(a) { return a.reduce((s, v) => s + v, 0) / a.length; }
function median(a) {
  const s = [...a].sort((x, y) => x - y);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}
function pstdev(a) {
  const m = mean(a);
  return Math.sqrt(mean(a.map(v => (v - m) ** 2)));
}

function DistTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="custom-tooltip">
      <div className="tt-time">{p.label} bbl</div>
      <div className="tt-value" style={{ fontWeight: 600 }}>{p.count} haul{p.count !== 1 ? 's' : ''}</div>
    </div>
  );
}

/**
 * VolumeDistribution — per-well histogram of DETECTED haul volumes (bbl).
 * Bin width and a focus range are adjustable; shows overall average plus the
 * average within the focus range (bars in the focus range are highlighted).
 */
function VolumeDistribution({ hauls, bblPerIn, numTanks, wellName }) {
  const [binW, setBinW] = useState(5);
  const [focusLo, setFocusLo] = useState(130);
  const [focusHi, setFocusHi] = useState(150);

  const vols = useMemo(() => {
    if (bblPerIn <= 0) return [];
    return hauls.map(h => h.haul_volume_ft * bblPerIn).filter(v => isFinite(v) && v > 0);
  }, [hauls, bblPerIn]);

  const stats = useMemo(() => {
    if (!vols.length) return null;
    const inFocus = vols.filter(v => v >= focusLo && v <= focusHi);
    return {
      n: vols.length,
      avg: mean(vols),
      median: median(vols),
      std: pstdev(vols),
      min: Math.min(...vols),
      max: Math.max(...vols),
      focusN: inFocus.length,
      focusAvg: inFocus.length ? mean(inFocus) : null,
      focusPct: (100 * inFocus.length) / vols.length,
    };
  }, [vols, focusLo, focusHi]);

  const bars = useMemo(() => {
    if (!vols.length) return [];
    const min = Math.floor(Math.min(...vols) / binW) * binW;
    const max = Math.ceil(Math.max(...vols) / binW) * binW;
    const nBins = Math.max(1, Math.round((max - min) / binW));
    const counts = new Array(nBins).fill(0);
    for (const v of vols) {
      let idx = Math.floor((v - min) / binW);
      if (idx >= nBins) idx = nBins - 1;
      if (idx < 0) idx = 0;
      counts[idx]++;
    }
    return counts.map((c, i) => {
      const lo = min + i * binW;
      const hi = lo + binW;
      const mid = lo + binW / 2;
      return {
        mid,
        label: `${lo.toFixed(0)}\u2013${hi.toFixed(0)}`,
        count: c,
        inFocus: mid >= focusLo && mid <= focusHi,
      };
    });
  }, [vols, binW, focusLo, focusHi]);

  if (bblPerIn <= 0) {
    return <div className="no-data">Set a tank diameter to compute barrel volumes.</div>;
  }
  if (!vols.length) {
    return <div className="no-data">No detected hauls for {wellName} in this range.</div>;
  }

  return (
    <div className="voldist-wrap">
      <div className="err-stats-row">
        <div className="err-stat">
          <div className="err-stat-label">Avg volume drawn</div>
          <div className="err-stat-value" style={{ color: AVG }}>{stats.avg.toFixed(1)} bbl</div>
        </div>
        <div className="err-stat">
          <div className="err-stat-label">Median</div>
          <div className="err-stat-value">{stats.median.toFixed(1)} bbl</div>
        </div>
        <div className="err-stat">
          <div className="err-stat-label">Std dev</div>
          <div className="err-stat-value">{stats.std.toFixed(1)} bbl</div>
        </div>
        <div className="err-stat">
          <div className="err-stat-label">Detected hauls</div>
          <div className="err-stat-value">{stats.n}</div>
        </div>
        <div className="err-stat">
          <div className="err-stat-label">
            Avg in {focusLo}{'\u2013'}{focusHi}
          </div>
          <div className="err-stat-value" style={{ color: BAR_FOCUS }}>
            {stats.focusAvg != null ? `${stats.focusAvg.toFixed(1)} bbl` : '\u2014'}
            <span className="voldist-focuspct">
              {' '}({stats.focusN}, {stats.focusPct.toFixed(0)}%)
            </span>
          </div>
        </div>
      </div>

      <div className="error-plot-card">
        <div className="voldist-head">
          <h2>Haul Volume Distribution — {wellName}</h2>
          <div className="voldist-controls">
            <label>
              Bin
              <select value={binW} onChange={e => setBinW(Number(e.target.value))}>
                <option value={2}>2 bbl</option>
                <option value={5}>5 bbl</option>
                <option value={10}>10 bbl</option>
                <option value={25}>25 bbl</option>
              </select>
            </label>
            <label>
              Focus
              <input type="number" value={focusLo} step={5}
                onChange={e => setFocusLo(Number(e.target.value))} />
              <span className="date-sep">{'\u2013'}</span>
              <input type="number" value={focusHi} step={5}
                onChange={e => setFocusHi(Number(e.target.value))} />
            </label>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={bars} margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
            <CartesianGrid stroke="#e1e0d9" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="mid"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={v => v.toFixed(0)}
              tick={{ fontSize: 11, fill: '#898781' }}
              label={{ value: 'Volume drawn (bbl)', position: 'insideBottom', offset: -10, fontSize: 12, fill: '#898781' }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#898781' }}
              label={{ value: 'Hauls', angle: -90, position: 'insideLeft', fontSize: 12, fill: '#898781' }}
            />
            <Tooltip content={<DistTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <ReferenceLine
              x={stats.avg}
              stroke={AVG}
              strokeWidth={2}
              label={{ value: `avg ${stats.avg.toFixed(0)}`, position: 'top', fontSize: 11, fill: AVG }}
            />
            <Bar dataKey="count" isAnimationActive={false}>
              {bars.map((b, i) => (
                <Cell key={i} fill={b.inFocus ? BAR_FOCUS : BAR} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="voldist-legend">
          <span><i style={{ background: BAR_FOCUS }} /> in focus range</span>
          <span><i style={{ background: BAR }} /> outside focus</span>
          <span><i style={{ background: AVG }} /> overall average</span>
        </div>
      </div>
    </div>
  );
}

export default VolumeDistribution;
